import { getCurrencySymbol, today } from "./formatters";
import { safeSheetName, sanitizeText, sanitizeRecordForExport } from "./security";
import { isMetalProfile, getProfileMetal } from "../constants";
import { activityLogExportRows } from "./activityLog";


function lendingAutoStatus(opening = 0, closing = 0, settledLabel = "Settled") {
  const start = Number(opening || 0);
  const paid = Number(closing || 0);
  if (start <= 0) return "No activity";
  if (paid <= 0) return "Pending";
  if (paid < start) return "Partial";
  return settledLabel;
}

function getLendingSummaryRows(profile, txns = []) {
  const sumType = (type) => txns.filter(t => t.txType === type).reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const lent = sumType("lent");
  const returned = sumType("returned");
  const borrowed = sumType("borrowed");
  const repaid = sumType("repaid");
  const profitAmount = txns.reduce((sum, t) => sum + Number(t.cashback || 0) + (t.txType === "profit" ? Number(t.amount || 0) : 0), 0);
  const receivable = Math.max(0, lent - returned);
  const payable = Math.max(0, borrowed - repaid);
  return [{
    Profile: profile?.name || "Lending Profile",
    "Lent Out": lent,
    Returned: returned,
    Borrowed: borrowed,
    Repaid: repaid,
    "Outstanding Receivable": receivable,
    "Outstanding Payable": payable,
    "Net Lending Position": receivable - payable,
    "Receivable Status": lendingAutoStatus(lent, returned, "Returned"),
    "Payable Status": lendingAutoStatus(borrowed, repaid, "Repaid"),
    "Profit Amount": profitAmount,
  }];
}

export async function exportToPDF(accounts, txnMap) {
  // Use the bundled jsPDF package instead of loading report code from a third-party CDN at runtime.
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const cs = getCurrencySymbol();
  const fmt2 = n => cs + new Intl.NumberFormat("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2}).format(Number(n)||0);
  const gram4 = n => { const num = Number(n || 0); return (Number.isFinite(num) ? Math.trunc(num * 10000) / 10000 : 0).toFixed(4); };
  let y = 20;

  // Header
  doc.setFontSize(20); doc.setTextColor(79,70,229);
  doc.text("FinVault - Financial Report", 20, y); y+=8;
  doc.setFontSize(10); doc.setTextColor(100,100,100);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 20, y); y+=12;

  accounts.forEach(acc => {
    const txns = txnMap[acc.id] || [];
    if (!txns.length) return;
    if (y > 260) { doc.addPage(); y = 20; }
    // Profile header
    doc.setFontSize(13); doc.setTextColor(30,27,75);
    doc.text(sanitizeText(`${acc.icon || ""} ${acc.name} (${acc.profileType})`, 90), 20, y); y+=6;
    doc.setDrawColor(200,200,220); doc.line(20,y,190,y); y+=6;

    if (acc.profileType==="standard") {
      const inc = txns.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0);
      const exp = txns.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0);
      doc.setFontSize(10); doc.setTextColor(22,163,74);
      doc.text(`Income: ${fmt2(inc)}`, 20, y);
      doc.setTextColor(220,38,38);
      doc.text(`Expense: ${fmt2(exp)}`, 80, y);
      doc.setTextColor(79,70,229);
      doc.text(`Balance: ${fmt2(inc-exp)}`, 140, y); y+=8;
    }
    if (acc.profileType==="lending") {
      const ls = getLendingSummaryRows(acc, txns)[0];
      doc.setFontSize(10); doc.setTextColor(220,38,38);
      doc.text(`Receivable: ${fmt2(ls["Outstanding Receivable"])} (${ls["Receivable Status"]})`, 20, y);
      doc.setTextColor(124,58,237);
      doc.text(`Payable: ${fmt2(ls["Outstanding Payable"])} (${ls["Payable Status"]})`, 100, y); y+=6;
      doc.setTextColor(ls["Net Lending Position"] >= 0 ? 5 : 220, ls["Net Lending Position"] >= 0 ? 150 : 38, ls["Net Lending Position"] >= 0 ? 105 : 38);
      doc.text(`Net Position: ${ls["Net Lending Position"] >= 0 ? "+" : "-"}${fmt2(Math.abs(ls["Net Lending Position"]))}`, 20, y);
      doc.setTextColor(124,58,237);
      doc.text(`Profit Amount: ${fmt2(ls["Profit Amount"])}`, 100, y); y+=8;
    }
    if (acc.profileType==="trading") {
      const pnl = txns.filter(t=>t.txType==="profit").reduce((s,t)=>s+t.amount,0)
                - txns.filter(t=>t.txType==="loss").reduce((s,t)=>s+t.amount,0);
      doc.setFontSize(10); doc.setTextColor(pnl>=0?22:220, pnl>=0?163:38, pnl>=0?74:38);
      doc.text(`Net P&L: ${pnl>=0?"+":""}${fmt2(pnl)}`, 20, y); y+=8;
    }
    if (isMetalProfile(acc.profileType)) {
      const metalSummary = metal => {
        const items = txns.filter(t=>(t.metalType||getProfileMetal(acc.profileType)||"Gold")===metal);
        const bought = items.filter(t=>t.txType==="buy").reduce((a,t)=>a+Number(t.grams||0),0);
        const sold = items.filter(t=>t.txType==="sell").reduce((a,t)=>a+Number(t.grams||0),0);
        const adjusted = items.filter(t=>t.txType==="adjustment").reduce((a,t)=>a+Number(t.grams||0),0);
        const buyValue = items.filter(t=>t.txType==="buy").reduce((a,t)=>a+Number(t.amount||0),0);
        const soldValue = items.filter(t=>t.txType==="sell").reduce((a,t)=>a+Number(t.amount||0),0);
        const avg = bought > 0 ? buyValue / bought : 0;
        const holding = bought - sold + adjusted;
        const holdingValue = Math.max(0, holding * avg);
        const realised = soldValue - (sold * avg);
        return { holding, holdingValue, soldValue, realised, avg };
      };
      const gold = metalSummary("Gold");
      const silver = metalSummary("Silver");
      const soldValue = gold.soldValue + silver.soldValue;
      const remainingHoldingCost = gold.holdingValue + silver.holdingValue;
      doc.setFontSize(10); doc.setTextColor(202,138,4);
      doc.text(`Gold: ${gram4(gold.holding)}g · Avg/g: ${fmt2(gold.avg||0)} · Holding cost: ${fmt2(gold.holdingValue)}`, 20, y);
      doc.setTextColor(100,116,139);
      doc.text(`Silver: ${gram4(silver.holding)}g · Avg/g: ${fmt2(silver.avg||0)} · Holding cost: ${fmt2(silver.holdingValue)}`, 20, y+6); y+=14;
      doc.setTextColor(15,118,110);
      doc.text(`Remaining holding cost: ${fmt2(remainingHoldingCost)}`, 20, y); y+=6;
      doc.setTextColor(37,99,235);
      doc.text(`Sold value: ${fmt2(soldValue)}`, 20, y); y+=6;
      doc.setTextColor(gold.realised>=0?22:220, gold.realised>=0?163:38, gold.realised>=0?74:38);
      doc.text(`Gold realized P/L: ${gold.realised>=0?"+":""}${fmt2(gold.realised)}`, 20, y); y+=6;
      doc.setTextColor(silver.realised>=0?22:220, silver.realised>=0?163:38, silver.realised>=0?74:38);
      doc.text(`Silver realized P/L: ${silver.realised>=0?"+":""}${fmt2(silver.realised)}`, 20, y); y+=8;
    }
    if (acc.profileType==="fdrd") {
      const invested = txns.reduce((a,t)=>a+Number(t.amount||0),0);
      const expected = txns.reduce((a,t)=>a+Number(t.expectedMaturityAmount||0),0);
      const expectedPct = invested > 0 ? ((expected - invested) / invested) * 100 : 0;
      doc.setFontSize(10); doc.setTextColor(15,118,110);
      doc.text(`Invested: ${fmt2(invested)}`, 20, y);
      doc.text(`Expected: ${fmt2(expected)} (${expectedPct.toFixed(2)}%)`, 80, y); y+=8;
    }
    // Recent 5 txns
    doc.setFontSize(9); doc.setTextColor(100,100,100);
    txns.slice(0,5).forEach(t => {
      if(y>270){doc.addPage();y=20;}
      const label = sanitizeText(t.category||t.txType||"", 30);
      const amt = `${fmt2(t.amount)}`;
      doc.text(sanitizeText(`  • ${t.date}  ${label}  ${t.note||""}`, 55), 20, y);
      doc.text(amt, 170, y); y+=5;
    });
    if (txns.length > 5) { doc.text(`  ... and ${txns.length-5} more transactions`, 20, y); y+=5; }
    y+=6;
  });

  doc.save(`FinVault_Report_${new Date().toISOString().split("T")[0]}.pdf`);
}

function csvCell(value) {
  const safe = value === null || value === undefined ? "" : String(value);
  return `"${safe.replace(/"/g, '""')}"`;
}

function rowsToCsv(title, rows) {
  const safeRows = (rows || []).map(sanitizeRecordForExport);
  const lines = [];
  lines.push(csvCell(title));
  if (!safeRows.length) {
    lines.push(csvCell("No data"));
    lines.push("");
    return lines.join("\r\n");
  }
  const headers = Array.from(new Set(safeRows.flatMap(row => Object.keys(row))));
  lines.push(headers.map(csvCell).join(","));
  safeRows.forEach(row => {
    lines.push(headers.map(h => csvCell(row[h])).join(","));
  });
  lines.push("");
  return lines.join("\r\n");
}

function downloadCsv(filename, csvText) {
  const blob = new Blob(["\ufeff" + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function exportToExcel(accounts, txnMap, activityLogMap = {}) {
  // Phase 92: replaced SheetJS/xlsx with CSV export to remove an unresolved npm audit finding.
  // The file opens in Excel/Google Sheets and still uses sanitizeRecordForExport for formula-injection protection.
  const sections = [];
  let hasRows = false;
  accounts.forEach(acc => {
    const txns = txnMap[acc.id] || [];
    const pt = acc.profileType;
    const rows = txns.map(t => {
      if (pt==="standard") return { Profile:acc.name, Date:t.date, Type:t.type, Category:t.category, Description:t.note||"", Amount:t.amount };
      if (pt==="lending")  return { Profile:acc.name, Date:t.date, Type:t.txType==="profit"?"profit amount":t.txType, Person:t.person||"", Note:t.note||"", Amount:t.txType==="profit"?0:t.amount, "Profit Amount":Number(t.cashback||0)+(t.txType==="profit"?Number(t.amount||0):0), "Due Date":t.dueDate||"" };
      if (isMetalProfile(pt)) return { Profile:acc.name, Date:t.date, Type:t.txType, Metal:t.metalType||getProfileMetal(pt)||"Gold", Grams:t.grams||0, Amount:t.amount, "Price/g":t.pricePerGram||"", Cashback:t.cashback||0, Platform:t.platform||"", Note:t.note||"" };
      if (pt==="fdrd") return { Profile:acc.name, Date:t.date, Status:t.txType||"active", "Deposit Type":t.depositType||"FD", Bank:t.bankName||"", Name:t.depositName||"", Invested:t.amount||0, "Interest %":t.interestRate||0, "Maturity Date":t.maturityDate||"", "Expected Maturity":t.expectedMaturityAmount||0, "Expected Return %":t.expectedReturnPct||0, "Actual Returned":t.actualReturnedAmount||0, "Actual Return %":t.actualReturnPct||0, Penalty:t.penalty||0, Note:t.note||"" };
      return { Profile:acc.name, Date:t.date, Type:t.txType, Note:t.note||"", Amount:t.amount };
    });
    if (rows.length > 0) hasRows = true;
    sections.push(rowsToCsv(safeSheetName(acc.name, "Profile"), rows));
    if (pt==="lending") {
      sections.push(rowsToCsv(safeSheetName(`${acc.name}_Lending_Summary`, "Lending_Summary"), getLendingSummaryRows(acc, txns)));
    }
    if (pt==="trading" && (acc.cvHistory||[]).length > 0) {
      hasRows = true;
      sections.push(rowsToCsv(safeSheetName(`${acc.name}_Val`, "Values"), acc.cvHistory.map(h=>({ Profile:acc.name, Date:h.date, "Portfolio Value":h.value }))));
    }
  });
  const logRows = activityLogExportRows(accounts, activityLogMap);
  if (logRows.length > 0) {
    hasRows = true;
    sections.push(rowsToCsv("Activity_Log_30d", logRows));
  }
  if (!hasRows) sections.push(rowsToCsv("Empty", [{ Note:"No data" }]));
  downloadCsv(`FinVault_${today()}.csv`, sections.join("\r\n"));
}
